<script setup>
import moment from 'moment';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ActionButton from './ActionButton.vue';
import ViewModal from './ViewModal.vue';
import CustomTransition from '@/_company/components/CustomTransition.vue';
import DialogModal from '@/_company/components/DialogModal.vue';
import ErrorModal from '@/_company/components/ErrorModal.vue';

const { te, t } = useI18n();

const showRevokeModal = ref(false);
const revokeModalIsLoading = ref(false);
const showConfirmRevokeModal = ref(false);
const showViewModal = ref(false);
const certId = ref('');
const certificate = ref('');
const errorMessage = ref('');

const props = defineProps({
  certificates: {
    type: Array,
    default: () => [],
  },

  showActions: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();

const handleCellAction = (action, value) => {
  if (action === 'download') {
    store.dispatch('company/certificates/downloadCert', value);
  } else if (action === 'revoke') {
    certId.value = value;
    showRevokeModal.value = true;
  } else if (action === 'view') {
    certificate.value = value;
    showViewModal.value = true;
  }
};

const getFormattedRequestedByValue = (value) => {
  const dashPosition = value.length === 10 ? 6 : 8;
  return [value.slice(0, dashPosition), '-', value.slice(dashPosition)].join('');
};

const getFormattedDate = (value) => moment(value).format('DD MMM YYYY, HH:mm');

const clearRevokeData = () => {
  showRevokeModal.value = false;
  certId.value = '';
};

const clearViewData = () => {
  showViewModal.value = false;
  certificate.value = '';
};

const revokeCertificate = async (id) => {
  revokeModalIsLoading.value = true;

  try {
    await store.dispatch('company/certificates/revokeCert', id);
    showConfirmRevokeModal.value = true;
  } catch (error) {
    errorMessage.value = error.message;
  }

  revokeModalIsLoading.value = false;
  showRevokeModal.value = false;
};

const sortTable = (table) =>
  table.sort((a, b) => {
    const rowA = a.cells.find((cell) => cell.id === 'status');
    const rowB = b.cells.find((cell) => cell.id === 'status');

    if (rowA.status === 'valid' && rowB.status === 'revoked') {
      return -1;
    }

    if (rowA.status === 'revoked' && rowB.status === 'valid') {
      return 1;
    }

    return 0;
  });

const getStatusValue = (value) => {
  const translationString = `company.certificates.${value.toLowerCase()}`;

  // te checks if translation exist before we try to write it out
  return te(translationString) ? t(translationString) : value.toLowerCase();
};

const table = computed(() =>
  sortTable(
    props.certificates.map((cert) => ({
      id: cert.id,
      cells: [
        { id: 'issued', value: getFormattedDate(cert.beginTime) },
        { id: 'validThrough', value: getFormattedDate(cert.endTime) },
        { id: 'issuer', value: cert.issuerBic },
        { id: 'requestedBy', value: getFormattedRequestedByValue(cert.requestedBy) },
        {
          id: 'status',
          value: getStatusValue(cert.status),
          status: cert.status.toLowerCase(),
          class: ['status', cert.status.toLowerCase()],
        },
      ],
      actionCells: [
        {
          id: 'download',
          value: cert,
          action: 'download',
          icon: 'download',
        },
        {
          id: 'view',
          value: cert,
          action: 'view',
          icon: 'view',
        },
        {
          id: 'revoke',
          value: cert.id,
          action: 'revoke',
          icon: 'revoke',
        },
      ],
    })),
  ),
);
</script>

<template>
  <custom-transition>
    <!-- Mobile and tablet only -->
    <div class="certificate-mobile wrapper" v-for="row in table" :key="row.id">
      <div class="cells">
        <div class="cell" :class="cell.class" v-for="cell in row.cells" :key="cell.id">
          <span class="heading">{{ t(`company.certificates.${cell.id}`) }}</span>
          <span>{{ cell.value }}</span>
        </div>
      </div>

      <div class="action-cells" v-if="showActions">
        <template v-for="cell in row.actionCells" :key="cell.id">
          <div class="action-cell">
            <action-button :cell="cell" :row="row" @onClick="handleCellAction" />
          </div>
        </template>
      </div>
    </div>

    <!-- Desktop only -->
    <div class="certificate-desktop" v-if="table && table.length">
      <table class="certificates-desktop wrapper">
        <thead>
          <tr>
            <th v-for="cell in table[0].cells" :key="cell.id">
              {{ t(`company.certificates.${cell.id}`) }}
            </th>

            <template v-if="showActions">
              <th v-for="cell in table[0].actionCells" :key="cell.id">
                {{ t(`company.certificates.${cell.id}`) }}
              </th>
            </template>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in table" :key="row.id">
            <td :class="cell.class" v-for="cell in row.cells" :key="cell.id">
              <span>{{ cell.value }}</span>
            </td>

            <template v-if="showActions">
              <td v-for="cell in row.actionCells" :key="cell.id">
                <action-button
                  :id="`${cell.id}-button`"
                  :cell="cell"
                  :row="row"
                  @onClick="handleCellAction"
                />
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Confirm revoke certificate modal -->
    <dialog-modal
      v-if="showRevokeModal"
      @cancel="clearRevokeData()"
      @confirm="revokeCertificate(certId)"
      :title="t('company.certificates.revokeModal.heading')"
      :message="t('company.certificates.revokeModal.text')"
      :confirmText="t('company.certificates.revokeModal.confirmButton')"
      :cancelText="t('company.certificates.revokeModal.cancelButton')"
      :isLoading="revokeModalIsLoading"
      confirmBgColor="red"
    />

    <!-- Revoke confirmation modal -->
    <dialog-modal
      v-if="showConfirmRevokeModal"
      @confirm="showConfirmRevokeModal = false"
      :title="t('company.certificates.revokeModal.confirmHeading')"
      :message="t('company.certificates.revokeModal.confirmText')"
      confirmText="Ok"
      :hideCancelButton="true"
      confirmBgColor="blue"
    />

    <!-- View modal -->
    <view-modal
      v-if="showViewModal"
      :certificate="certificate.certEncoded"
      @onConfirm="() => store.dispatch('company/certificates/downloadCert', certificate)"
      @onCancel="clearViewData()"
    />

    <!-- Error modal -->
    <error-modal
      v-if="errorMessage"
      :title="t('company.error.heading')"
      :text="errorMessage"
      @onConfirm="errorMessage = ''"
    />
  </custom-transition>
</template>

<style lang="scss" scoped>
$cell-space-vertical: $space-40;

.wrapper {
  box-shadow: $shadow-low-elevation;
  border-radius: $border-radius-x-small;
  background: $primary-background;
}

.certificate-desktop {
  display: none;

  @include desktop-small {
    display: block;
  }
}

table {
  border-collapse: collapse;
  width: 100%;
}

tr {
  border-bottom: 1px solid $primary-border;
}

thead {
  tr {
    @include small-caption-bold;
    color: $secondary-text;
    text-transform: uppercase;

    th {
      padding-top: $space-32;
      padding-bottom: $space-16;
      text-align: left;
    }
  }
}

td {
  @include secondary-text-regular;
}

td,
th {
  padding: $space-24;

  &:first-child {
    padding-left: $cell-space-vertical;
  }

  &:last-child {
    padding-right: $cell-space-vertical;
  }
}

.certificate-mobile {
  margin-bottom: $space-24;
  padding: 6px $space-24 6px $space-16;

  @include desktop-small {
    display: none;
  }

  .heading {
    @include small-caption-bold;
    color: $secondary-text;
    text-transform: uppercase;
    margin-right: calc($space-16 / 2);
  }

  .cell {
    @include caption-regular;
    padding: $space-16 0;
    display: flex;
    align-items: baseline;

    &:nth-child(2n) {
      padding-top: 0;
      margin-top: -$space-12;
      border-bottom: 1px solid $primary-border;
    }
  }

  .action-cells {
    padding: 0 0 $space-16;
    border-top: 1px solid $primary-border;
    display: flex;
    flex-wrap: wrap; // For iphone 5 size, icons do not fit

    .action-cell {
      margin-right: $space-16;
      margin-top: $space-16; // For iphone 5 size, icons do not fit

      &:last-of-type {
        margin-right: 0;
      }

      &:empty {
        display: none;
      }
    }
  }
}

td.status,
.cell.status {
  @include secondary-text-medium;
  text-transform: capitalize;

  &.revoked {
    color: $negative-text;
  }

  &.valid {
    color: $positive-text;
  }
}
</style>
